export const LOGIN_REQUEST = "LOGIN_REQUEST"
export const LOGIN_SUCCESS = "LOGIN_SUCCESS"
export const LOGIN_FAILURE = "LOGIN_FAILURE"
export const LOGOUT = "LOGOUT"

export type Role = "user" | "admin" | "superAdmin"

export type LoginResponseType = {
  data: LoginResponseData
  status: number
  message: string
}

export type LoginResponseData = {
  app_code?: string
  email: string
  log_id: number
  mobile: string
  prop_id: number
  user_id: number
  language: string
  full_name: string
  prop_code: string
  prop_name: string
  telephone: string
  user_name: string
  login_time: string
  result_msg: string
  profile_pic: string
  result_code: number
  access_token: string
  job_position: string
  license_info: LicenseInfo
  current_shift: string | null
  dashboard_url: string
  prop_group_id: number | null
  system_config: SystemConfig
  user_group_id: number
  prop_group_name: string | null
  user_group_name: string
  user_group_type: string
  user_permission: UserPermission[]
}

export type LicenseInfo = {
  active: boolean
  warning: boolean
  is_expire: boolean
  expire_date: string
  warning_title: string
  renew_link_url: string
  renew_link_text: string
  warning_message: string
}

export type SystemConfig = {
  pass_policy: PassPolicy
  system_params: SystemParams
  printform_info: PrintformInfo[]
  session_timeout: string
}

export type PassPolicy = {
  reg_exp: string
  alert_text: string
  max_length: number
  min_length: number
}

export type SystemParams = {
  TAX: {
    VAT_RATE: string
    ISSUE_TAX: boolean
    FORCE_ISSUE_TAX: boolean
    VAT_ISSUE_ON_PAYMENT: boolean
    VAT_ITEM_DESCRIPTION: string
  }
  FOLIO: {
    FOLIO_FORMAT: number
    FOLIO_OPTION: unknown | null
    FOLIO_EDITABLE: boolean
    FOLIO_DEL_TRANS: boolean
    FOLIO_GEN_TRANS: boolean
    FOLIO_GRP_TRANS: boolean
    FOLIO_INS_TRANS: boolean
    FOLIO_MRG_TRANS: boolean
    FOLIO_EDIT_HEADER: boolean
    FOLIO_EDIT_ROOMNO: boolean
    FOLIO_PRINT_EMPTY: boolean
    FOLIO_EDIT_ARR_DEP: boolean
    FOLIO_EDIT_BALANCE: boolean
  }
  BACKUP: {
    BACKUPDAY: number
    BACKUP_DAYS: string
    CLEARING_DAYS: string
    DBBACKUP_SERVER: string
    DBBACKUP_DAYKEEP: string
    DBBACKUP_DATABASE: string
  }
  ENDDAY: {
    CHECK_AUTOPRINT: boolean
    ENDDAY_SKIP_PRINT: boolean
    ENDDAY_SKIP_BACKUP: boolean
    ENDDAY_CHECK_DEPOSIT: boolean
  }
  SYSTEM: {
    SHIFTS: string[]
    LANGUAGE: string
    TIMEZONE: string
    NAME_FORMAT: string
    ONLINE_POST: boolean
    SYSTEM_DATE: string
    ONLINE_SHIFT: string
    AUTOPRINTFLAG: boolean
    ALLOW_OVERBOOK: boolean
    ENDDAY_RUNNING: boolean
    ALLOW_JOIN_ROOM: boolean
    AUTO_BLOCK_ROOM: boolean
    NAME_FORCE_UPPER: boolean
    REVENUE_CALC_TYPE: number
    AUTO_CONFIRM_BOOKING: boolean
  }
  CASHIER: {
    CREDIT_CARD_MASK: string
    CLOSE_SHIFT_BY_USER: boolean
  }
  DEFAULT: {
    VIP: number
    TITLE: number
    ORIGIN: number
    ADULT_QTY: number
    CHILD_QTY: string
    ITEMIZERS: string[]
    THAINATION: string
    TITLE_MALE: number
    TRXDEPOSIT: boolean
    APPROVERATE: boolean
    MSG_DAYKEEP: number
    NATIONALITY: number
    ACCOM_FORMAT: string
    CHECKIN_TIME: string
    CREDIT_LIMIT: string
    TITLE_FEMALE: number
    CHECKOUT_TIME: string
    CONTRACT_NOTE: boolean
    FOLIO_PATTERN: number
    CONFIRM_LETTER: string
    PAYMENT_METHOD: string
    ABF_COMPBY_FLAG: boolean
    ALLOTBYROOMTYPE: boolean
    WALKIN_RATEPLAN: unknown | null
    RESERVATION_TYPE: number
    ROOM_CHARGE_DEPT: number
  }
  DEPOSIT: {
    OPEN_FOLIO: boolean
    CANCEL_DEPT_ID: number
    NOSHOW_DEPT_ID: number
    DPS_NOSHOW_CODE: string
    DPS_REFUND_DEPT: string
    DPS_RECEIPT_CODE: string
    DPS_REFUND_MEDIA: string
    DPS_CLEARING_CODE: string
    REFUND_PAYMENT_ID: number
    DEFAULT_PAYMENT_ID: number
    DPS_RECEIPTNO_AUTO: boolean
  }
  CALENDAR: {
    WEEKEND_DAYS: {
      fri: boolean
      mon: boolean
      sat: boolean
      sun: boolean
      thu: boolean
      tue: boolean
      wed: boolean
    }
    FIRST_MONTH_FISCAL: string
    FIRST_MONTH_TOURISM: string
  }
  AUTO_POST: {
    ABF: number
    ROOM: number
    LUNCH: number
    DINNER: number
    EXTRABED: number
  }
  LOG_TYPES: LogTypeCategory[]
  ROOM_RATE: {
    TAX_RATE: number
    EXTRA_ADULT: string
    EXTRA_CHILD: string
    EXTRA_INFANT: number
    SERVICE_RATE: number
    INCLUDE_ADULT: number
    TAX_ON_SERVICE: boolean
    WEEKLY_RATE_MODE: number
    MONTHLY_RATE_MODE: number
    WEEKLY_RATE_ENABLED: boolean
    MONTHLY_RATE_ENABLED: boolean
  }
  interfaces: InterfacesConfig
  RESERVATION: {
    ALLOW_OVERBOOK: boolean
    ALLOTMENT_ENABLED: boolean
    AUTO_CONFIRM_BOOKING: boolean
  }
  ROOM_STATUS: RoomStatusConfig
  DASHBOARD_URL: string
  GUEST_PROFILE: {
    NAME_FORMAT: string
    NAME_AUTO_MATCH: boolean
    NAME_FORCE_UPPER: boolean
    CHECK_OVERALL_AVAIL: boolean
  }
  LOCAL_SERVICE_DOWNLOAD_URL: string
}

export type LogTypeCategory = {
  category: string
  log_types: string[]
}

export type InterfacesConfig = {
  BE: boolean
  CN: boolean
  RR4: boolean
  NBIS: boolean
  TM30: boolean
  IDCARD: boolean
  KEYCARD: boolean
  PASSPORT: boolean
  SALESTAX: boolean
  "MSG-CHECKIN": boolean
  "MSG-CHECKOUT": boolean
}

export type RoomStatusConfig = {
  COLORS: Record<string, string> // e.g. { CL: "#038FDE", ... }
  SELLABLE: string[]
  IS_ENABLED: boolean
  MI_ENABLED: boolean
  PU_ENABLED: boolean
  OOI_ENABLED: boolean
  OOS_ENABLED: boolean
  RETURN_STATUS: string
  PICKUP_ENABLED: boolean
  CHECKOUT_STATUS: string
  CHECK_OUT_STATUS: string
  CLEAN_IS_SELLABLE: boolean
  INSPECTED_ENABLED: boolean
  INSPECTED_IS_SELLABLE: boolean
}

export type PrintformInfo = {
  id: number
  name: string
  options: boolean
  filename: string
  path_url: string | null
  form_type: string | null
}

export type UserPermission = {
  code: string
  name: string
}

export type User = {
  id: string
  name: string
  role: Role,
  profilePic?: string | null
  userPermission?: UserPermission[]
}

export type AuthState = {
  loading: boolean
  user: User | null
  propCode?: string | null
  token: string | null
  error: string | null
}

export type LoginRequestAction = {
  type: typeof LOGIN_REQUEST
  payload: {
    username: string
    password: string
    propCode: string
  }
}

export type LoginSuccessAction = {
  type: typeof LOGIN_SUCCESS
  payload: {
    user: User
    token: string
    propCode: string
  }
}

export type LoginFailureAction = {
  type: typeof LOGIN_FAILURE
  payload: string
}

export type LogoutAction = {
  type: typeof LOGOUT
}

export type AuthAction = LoginRequestAction | LoginSuccessAction | LoginFailureAction | LogoutAction
